import { useCallback, useEffect, useRef, useState } from 'react'
import type { FilterOperator, FilterValues } from '@/components/problems/types'
import { defaultFilters } from '@/components/problems/types'
import type { ListRef, ProblemsPageState } from '@/lib/url-state'
import {
  deserializeFromParams,
  mergeManagedParams,
  problemsPageStateSchema,
  readSessionState,
  resolveState,
  serializeToParams,
  writeSessionState,
} from '@/lib/url-state'

interface ExternalSort {
  sortBy: string
  sortDir: 'asc' | 'desc'
}

interface UseProblemsStateReturn {
  // State
  filters: FilterValues
  sortBy: string
  sortDir: 'asc' | 'desc'
  searchQuery: string
  matchMode: 'any' | 'all'
  showTags: boolean
  missingLists: string[]

  // Setters (sort setters undefined when externalSort is provided)
  setFilters: React.Dispatch<React.SetStateAction<FilterValues>>
  setSortBy: ((value: string) => void) | undefined
  setSortDir: ((value: 'asc' | 'desc') => void) | undefined
  setSearchQuery: (value: string) => void
  setMatchMode: (value: 'any' | 'all') => void
  setShowTags: React.Dispatch<React.SetStateAction<boolean>>
  toggleFilterValue: (
    field: 'status' | 'difficulty' | 'topics' | 'lists',
    value: string,
  ) => void
  resetFilters: () => void
  setFilterOperator: (
    field: 'status' | 'difficulty' | 'topics' | 'lists',
    operator: FilterOperator,
  ) => void
}

/**
 * Manages filter/sort/search state with dual-write to URL params and sessionStorage.
 *
 * On mount: URL params > sessionStorage > defaults.
 * On change: React state + sessionStorage + URL replaceState.
 *
 * IMPORTANT: `userLists` and `availableTopics` must be referentially stable
 * (memoized with useMemo) to avoid unnecessary re-resolution.
 *
 * @param storageKey - sessionStorage key (e.g. 'bctci-browse-table' or 'bctci-list-detail-{id}')
 * @param userLists - user's lists for slug resolution (must be memoized)
 * @param availableTopics - available topic names for slug resolution (must be memoized)
 * @param validSortOptions - valid sort option values for this page context
 * @param externalSort - when provided, sort is read-only (for DB-driven list sort)
 */
export function useProblemsState(
  storageKey: string,
  userLists: ListRef[],
  availableTopics: string[],
  validSortOptions: string[],
  externalSort?: ExternalSort,
): UseProblemsStateReturn {
  const isBrowser = typeof window !== 'undefined'
  // Remember mount pathname so we only write URL params on our own page
  const mountPathnameRef = useRef(isBrowser ? window.location.pathname : '')

  // --- Initial resolution (mount-time, runs once) ---
  const urlParamsRef = useRef(
    deserializeFromParams(
      isBrowser
        ? new URLSearchParams(window.location.search)
        : new URLSearchParams(),
    ),
  )
  const initialRef = useRef<{
    state: ProblemsPageState
    missingLists: string[]
  } | null>(null)

  if (!initialRef.current) {
    const sessionState = readSessionState(storageKey, (raw) => {
      const parsed = problemsPageStateSchema.safeParse(raw)
      return parsed.success ? parsed.data : null
    })
    initialRef.current = resolveState(
      urlParamsRef.current,
      sessionState,
      userLists,
      availableTopics,
      validSortOptions,
    )
  }

  // State
  const [filters, setFilters] = useState<FilterValues>(
    initialRef.current.state.filters,
  )
  const [sortBy, _setSortBy] = useState(initialRef.current.state.sortBy)
  const [sortDir, _setSortDir] = useState(initialRef.current.state.sortDir)
  const [searchQuery, setSearchQueryRaw] = useState(
    initialRef.current.state.searchQuery,
  )
  const [matchMode, setMatchModeRaw] = useState(
    initialRef.current.state.matchMode,
  )
  const [showTags, setShowTagsRaw] = useState(initialRef.current.state.showTags)
  const [missingLists, setMissingLists] = useState(
    initialRef.current.missingLists,
  )

  // --- Re-resolve when async deps arrive ---
  // If userLists or availableTopics were empty on mount (tRPC queries not yet resolved)
  // but URL had list/topic params, we need to re-resolve once data is available.
  const hasReResolvedRef = useRef(false)

  useEffect(() => {
    // Only re-resolve if we had URL params with lists/topics AND deps were empty at mount
    if (hasReResolvedRef.current) return
    if (!urlParamsRef.current) return

    const hadListParams =
      (urlParamsRef.current.filters?.lists?.values.length ?? 0) > 0
    const hadTopicParams =
      (urlParamsRef.current.filters?.topics?.values.length ?? 0) > 0

    if (!hadListParams && !hadTopicParams) return
    if (
      (hadListParams && userLists.length === 0) ||
      (hadTopicParams && availableTopics.length === 0)
    ) {
      return // still waiting for data
    }

    // Data is now available — re-resolve
    hasReResolvedRef.current = true
    const { state, missingLists: missing } = resolveState(
      urlParamsRef.current,
      null, // URL wins, ignore session
      userLists,
      availableTopics,
      validSortOptions,
    )

    // Sync only the fields this async data was needed to resolve: the
    // list/topic filters (slug→id mapping needs userLists/availableTopics) and,
    // when sort is internally controlled, sort. Do NOT re-apply
    // searchQuery/matchMode/showTags — they never depended on this data and were
    // already resolved at mount, so re-applying the mount-time URL snapshot here
    // would clobber any edit the user made to them while the data was loading.
    setFilters(state.filters)
    setMissingLists(missing)

    // Only update sort state when it is internally controlled
    if (!externalSort) {
      _setSortBy(state.sortBy)
      _setSortDir(state.sortDir)
    }
  }, [userLists, availableTopics, validSortOptions, externalSort])

  // --- Clear missingLists on user-initiated state changes ---
  // Set by setter functions (not by internal effects like re-resolution)
  const userChangedRef = useRef(false)
  // biome-ignore lint/correctness/useExhaustiveDependencies: Re-run when any user-controlled facet changes so we clear missingLists after filters/sort/search updates; missingLists alone is insufficient.
  useEffect(() => {
    if (userChangedRef.current && missingLists.length > 0) {
      setMissingLists([])
    }
    userChangedRef.current = false
  }, [filters, sortBy, sortDir, searchQuery, matchMode, missingLists])

  // --- Refs for latest values (avoids stale closures in sync effect) ---
  const userListsRef = useRef(userLists)
  userListsRef.current = userLists

  // --- Build current state for sync (used by both effects below) ---
  const currentStateRef = useRef<ProblemsPageState>(null!)
  currentStateRef.current = {
    searchQuery,
    matchMode,
    filters,
    sortBy: externalSort?.sortBy ?? sortBy,
    sortDir: externalSort?.sortDir ?? sortDir,
    showTags,
  }

  // --- Sync sessionStorage + URL on state change ---
  // biome-ignore lint/correctness/useExhaustiveDependencies: Effect must re-run when serialized page state changes; reads latest values via currentStateRef inside the effect.
  useEffect(() => {
    const currentState = currentStateRef.current

    // Always write sessionStorage (persists even when navigating away)
    writeSessionState(storageKey, currentState)

    // Only write URL when still on our page (avoids leaking params to other routes)
    if (window.location.pathname !== mountPathnameRef.current) return

    const managed = serializeToParams(currentState, userListsRef.current)
    const merged = mergeManagedParams(window.location.search, managed)
    const search = merged.toString()
    const newUrl = search
      ? `${window.location.pathname}?${search}`
      : window.location.pathname
    window.history.replaceState(window.history.state, '', newUrl)
  }, [
    searchQuery,
    matchMode,
    filters,
    sortBy,
    sortDir,
    showTags,
    externalSort,
    storageKey,
  ])

  // --- URL guard: fix external URL changes (e.g. router nav to same page) ---
  // Runs after every render; only calls replaceState when there's a mismatch.
  useEffect(() => {
    if (window.location.pathname !== mountPathnameRef.current) return

    const managed = serializeToParams(
      currentStateRef.current,
      userListsRef.current,
    )
    const merged = mergeManagedParams(window.location.search, managed)
    const search = merged.toString()
    const expected = search ? `?${search}` : ''
    if (window.location.search !== expected) {
      window.history.replaceState(
        window.history.state,
        '',
        window.location.pathname + expected,
      )
    }
  })

  // --- Setters ---
  // Always call useCallback (rules-of-hooks); conditionally expose sort setters
  const setSortByFn = useCallback((value: string) => {
    userChangedRef.current = true
    _setSortBy(value)
  }, [])
  const setSortDirFn = useCallback((value: 'asc' | 'desc') => {
    userChangedRef.current = true
    _setSortDir(value)
  }, [])
  const setSortBy = externalSort ? undefined : setSortByFn
  const setSortDir = externalSort ? undefined : setSortDirFn

  const setSearchQuery = useCallback((value: string) => {
    userChangedRef.current = true
    setSearchQueryRaw(value)
  }, [])

  const setMatchMode = useCallback((value: 'any' | 'all') => {
    userChangedRef.current = true
    setMatchModeRaw(value)
  }, [])

  const setShowTags: React.Dispatch<React.SetStateAction<boolean>> =
    useCallback((value) => {
      userChangedRef.current = true
      setShowTagsRaw(value)
    }, [])

  const toggleFilterValue = useCallback(
    (field: 'status' | 'difficulty' | 'topics' | 'lists', value: string) => {
      userChangedRef.current = true
      setFilters((prev) => {
        const current = prev[field].values
        const next = current.includes(value)
          ? current.filter((v) => v !== value)
          : [...current, value]
        return { ...prev, [field]: { ...prev[field], values: next } }
      })
    },
    [],
  )

  const resetFilters = useCallback(() => {
    userChangedRef.current = true
    setFilters({ ...defaultFilters })
    setMatchModeRaw('all')
  }, [])

  const setFilterOperator = useCallback(
    (
      field: 'status' | 'difficulty' | 'topics' | 'lists',
      operator: FilterOperator,
    ) => {
      userChangedRef.current = true
      setFilters((prev) => ({
        ...prev,
        [field]: { ...prev[field], operator },
      }))
    },
    [],
  )

  return {
    filters,
    sortBy: externalSort?.sortBy ?? sortBy,
    sortDir: externalSort?.sortDir ?? sortDir,
    searchQuery,
    matchMode,
    showTags,
    missingLists,
    setFilters,
    setSortBy,
    setSortDir,
    setSearchQuery,
    setMatchMode,
    setShowTags,
    toggleFilterValue,
    resetFilters,
    setFilterOperator,
  }
}
